// TODO- old file need to delete when PRD-113 released flag: checkInRevamped
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import CustomButton from "components/common/Button";
import ButtonWithDropdown from "components/common/ButtonWithDropdown";
import Menu, { MenuItem } from "components/common/Menu";
import SelectShiftModal from "components/CheckInCheckOut/SelectShiftModal/SelectShiftModalOld";
import BreakTimeExceededModal from "components/CheckInCheckOut/BreakTimeExceededModal";
import EndDayModal from "components/CheckInCheckOut/EndDayModal/EndDayModal";
import ClockOutSuccessModal from "components/CheckInCheckOut/ClockOutSuccessModal/ClockOutSuccessModal";
import MissedClockOutModal from "components/CheckInCheckOut/MissedClockOutModal/MissedClockOutModal";
import { AlertType } from "components/common/Snackbar";

import { getAttendanceDetails, clockEntry } from "services/CheckInCheckOut/CheckInAndBreaks";
import { showSnackbar } from "store/actions/Utility";
import { hasPermission } from "hooks/usePermission";

import BurgerIcon from "assets/images/break-logo.svg?react";
import FeederIcon from "assets/images/nursing-logo.svg?react";
import WorkIcon from "assets/images/work-logo.svg?react";

import "./ClockInCard.scss";
import { useFlags } from "launchdarkly-react-client-sdk";

function ClockInCard({ onShiftConfirm = null, onAttendanceUpdate = null }) {
  const { checkInRevamped } = useFlags();
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const canRecordTime = hasPermission("timeTracking.record");
  const [openSelectShift, setOpenSelectShift] = useState(false);
  const [openBreakExceededModal, setOpenBreakExceededModal] = useState(false);
  const [openEndDayModal, setOpenEndDayModal] = useState(false);
  const [openClockOutSuccessModal, setOpenClockOutSuccessModal] = useState(false);
  const [openMissedClockOutModal, setOpenMissedClockOutModal] = useState(false);
  const [cachedDaySummary, setCachedDaySummary] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [hasSchedule, setHasSchedule] = useState(false);
  const [isNursingBreakAllowed, setIsNursingBreakAllowed] = useState(false);
  const openBreakMenu = Boolean(anchorEl);

  // Helper functions to calculate data from API
  const getIsClockedIn = useCallback(() => {
    if (!attendanceData) return false;
    return (
      attendanceData?.todayStatus?.isClockedIn || attendanceData?.attendanceDetails?.todayStatus?.isClockedIn || false
    );
  }, [attendanceData]);

  const getActiveBreak = useCallback(() => {
    if (!attendanceData) return null;
    const breaks = attendanceData?.data?.breaks || attendanceData?.attendanceDetails?.data?.breaks || [];
    return breaks.find((breakItem) => breakItem.inTime && !breakItem.outTime);
  }, [attendanceData]);

  const getBreakType = useCallback(() => {
    const activeBreak = getActiveBreak();
    if (!activeBreak) return null;
    return activeBreak.entryType === "nursing_break" ? "nursing_break" : "break";
  }, [getActiveBreak]);

  const isWithinShiftTime = useCallback(() => {
    const schedule = attendanceData?.schedule || attendanceData?.attendanceDetails?.schedule;
    if (!schedule?.startTime || !schedule?.endTime) return true; // If no schedule, allow breaks

    const attendanceDate =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;

    if (!attendanceDate) return true;

    const [startHours, startMinutes] = schedule.startTime.split(":").map(Number);
    const [endHours, endMinutes] = schedule.endTime.split(":").map(Number);

    const shiftDate = new Date(attendanceDate);
    const shiftStartTime = new Date(shiftDate);
    shiftStartTime.setHours(startHours, startMinutes, 0, 0);

    // Calculate early clock-in time (3 hours before shift start, capped at midnight)
    const earlyClockInTime = new Date(shiftDate);
    const earlyClockInHours = startHours >= 3 ? startHours - 3 : 0;
    const earlyClockInMinutes = startHours >= 3 ? startMinutes : 0;
    earlyClockInTime.setHours(earlyClockInHours, earlyClockInMinutes, 0, 0);

    const isOvernightShift = endHours < startHours || (endHours === startHours && endMinutes < startMinutes);

    const shiftEndTime = new Date(shiftDate);
    if (isOvernightShift) {
      // For overnight shifts, end time is on the next day
      shiftEndTime.setDate(shiftEndTime.getDate() + 1);
    }
    shiftEndTime.setHours(endHours, endMinutes, 0, 0);

    // Allow breaks from 3 hours before shift start until 3 hours after shift end time
    const threeHoursAfterShift = new Date(shiftEndTime);
    threeHoursAfterShift.setHours(threeHoursAfterShift.getHours() + 3);

    return currentTime >= earlyClockInTime.getTime() && currentTime <= threeHoursAfterShift.getTime();
  }, [attendanceData, currentTime]);

  const getTotalElapsedTime = () => {
    if (!attendanceData || !getIsClockedIn()) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    if (!attendanceInTime) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const inTime = new Date(attendanceInTime).getTime();
    const totalElapsed = Math.floor((currentTime - inTime) / 1000);

    const hours = Math.floor(totalElapsed / 3600);
    const minutes = Math.floor((totalElapsed % 3600) / 60);
    const seconds = totalElapsed % 60;

    return { hours, minutes, seconds };
  };

  const getWorkTime = () => {
    if (!attendanceData || !getIsClockedIn()) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    if (!attendanceInTime) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const inTime = new Date(attendanceInTime).getTime();
    const totalElapsed = Math.floor((currentTime - inTime) / 1000);
    const totalBreakSeconds =
      attendanceData?.stats?.totalBreakSeconds || attendanceData?.attendanceDetails?.stats?.totalBreakSeconds || 0;
    const totalNursingBreakSeconds =
      attendanceData?.stats?.totalNursingBreakSeconds ||
      attendanceData?.attendanceDetails?.stats?.totalNursingBreakSeconds ||
      0;
    const workSeconds = totalElapsed - totalBreakSeconds - totalNursingBreakSeconds;

    const hours = Math.floor(workSeconds / 3600);
    const minutes = Math.floor((workSeconds % 3600) / 60);
    const seconds = workSeconds % 60;

    return { hours, minutes, seconds };
  };

  const getCurrentBreakTime = () => {
    const activeBreak = getActiveBreak();
    if (!activeBreak) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const breakInTime = new Date(activeBreak.inTime).getTime();
    const elapsed = Math.floor((currentTime - breakInTime) / 1000);

    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;

    return { hours, minutes, seconds };
  };

  const getTotalBreakTime = () => {
    if (!attendanceData) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const totalBreakSeconds =
      attendanceData?.stats?.totalBreakSeconds || attendanceData?.attendanceDetails?.stats?.totalBreakSeconds || 0;
    const hours = Math.floor(totalBreakSeconds / 3600);
    const minutes = Math.floor((totalBreakSeconds % 3600) / 60);
    const seconds = totalBreakSeconds % 60;

    return { hours, minutes, seconds };
  };

  const getTotalNursingTime = () => {
    if (!attendanceData) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    const totalNursingBreakSeconds =
      attendanceData?.stats?.totalNursingBreakSeconds ||
      attendanceData?.attendanceDetails?.stats?.totalNursingBreakSeconds ||
      0;
    const hours = Math.floor(totalNursingBreakSeconds / 3600);
    const minutes = Math.floor((totalNursingBreakSeconds % 3600) / 60);
    const seconds = totalNursingBreakSeconds % 60;

    return { hours, minutes, seconds };
  };

  const checkIfClockOutMissed = () => {
    if (!attendanceData || !getIsClockedIn()) {
      return false;
    }

    // Get schedule data
    const scheduleData = attendanceData?.schedule || attendanceData?.attendanceDetails?.schedule;
    if (!scheduleData?.endTime || !scheduleData?.startTime) {
      return false;
    }

    // Get today's attendance in time
    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    if (!attendanceInTime) {
      return false;
    }

    // Parse shift times
    const [startHours, startMinutes] = scheduleData.startTime.split(":").map(Number);
    const [endHours, endMinutes] = scheduleData.endTime.split(":").map(Number);

    // Check if it's an overnight shift
    const isOvernightShift = endHours < startHours || (endHours === startHours && endMinutes < startMinutes);

    const shiftEndTime = new Date(attendanceInTime);
    if (isOvernightShift) {
      // For overnight shifts, end time is on the next day
      shiftEndTime.setDate(shiftEndTime.getDate() + 1);
    }
    shiftEndTime.setHours(endHours, endMinutes, 0, 0);

    // Calculate 3 hours after shift end
    const threeHoursAfterShiftEnd = new Date(shiftEndTime.getTime() + 3 * 60 * 60 * 1000);

    // Check if current time is more than 3 hours after shift end
    const now = new Date();
    return now > threeHoursAfterShiftEnd;
  };

  const getShiftEndTime = () => {
    const scheduleData = attendanceData?.schedule || attendanceData?.attendanceDetails?.schedule;
    if (!scheduleData?.endTime || !scheduleData?.startTime) {
      return null;
    }

    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    if (!attendanceInTime) {
      return null;
    }

    // Parse shift times
    const [startHours, startMinutes] = scheduleData.startTime.split(":").map(Number);
    const [endHours, endMinutes] = scheduleData.endTime.split(":").map(Number);

    // Check if it's an overnight shift
    const isOvernightShift = endHours < startHours || (endHours === startHours && endMinutes < startMinutes);

    const shiftEndTime = new Date(attendanceInTime);
    if (isOvernightShift) {
      shiftEndTime.setDate(shiftEndTime.getDate() + 1);
    }
    shiftEndTime.setHours(endHours, endMinutes, 0, 0);

    return shiftEndTime;
  };

  const getClockInTime = () => {
    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    return attendanceInTime ? new Date(attendanceInTime) : null;
  };

  const getShiftDate = () => {
    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    return attendanceInTime ? new Date(attendanceInTime) : new Date();
  };

  const fetchAttendanceDetails = useCallback(async () => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const employeeId = loggedInUser?.userId;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      const data = await getAttendanceDetails(employeeId);
      setAttendanceData(data);

      // Check if schedule exists - check both possible paths
      const scheduleData = data?.schedule || data?.attendanceDetails?.schedule || data?.data?.schedule;
      setHasSchedule(!!scheduleData);
      setIsNursingBreakAllowed(!!scheduleData?.isNursingBreakAllowed);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxDispatch]);

  // Fetch attendance details on component mount
  useEffect(() => {
    fetchAttendanceDetails();
  }, [fetchAttendanceDetails]);

  // Update current time every second for timer calculations
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTimeUnit = (value) => {
    return value.toString().padStart(2, "0");
  };

  const getTimerLabel = () => {
    const activeBreak = getActiveBreak();
    if (activeBreak) {
      return getBreakType() === "break" ? "Break Time" : "Nursing";
    }
    return "Work Time";
  };

  const formatTimestamp = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleClockIn = async () => {
    const isClockedIn = getIsClockedIn();

    if (!isClockedIn) {
      // Check if schedule exists
      if (!hasSchedule) {
        setOpenSelectShift(true);
      } else {
        // Perform clock in API call
        try {
          const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
          const employeeId = loggedInUser?.userId;

          if (!employeeId) {
            reduxDispatch(
              showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
            );
            return;
          }

          const timestamp = formatTimestamp(new Date());
          await clockEntry(
            employeeId,
            {
              action: "check_in",
              timestamp,
            },
            checkInRevamped
          );

          // Refetch attendance details after successful clock-in
          await fetchAttendanceDetails();

          reduxDispatch(
            showSnackbar({
              type: AlertType.success,
              message: "Successfully clocked in. Have a productive day!",
              dispatch: reduxDispatch,
            })
          );
        } catch (error) {
          reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
        }
      }
    } else if (checkIfClockOutMissed()) {
      // Clock out - Check if clock-out was missed (more than 3 hours after shift end)
      setOpenMissedClockOutModal(true);
    } else {
      setOpenEndDayModal(true);
    }
  };

  const handleSelectShiftClose = () => {
    setOpenSelectShift(false);
  };

  const handleSelectShiftConfirm = async (shiftData) => {
    setOpenSelectShift(false);

    // Pass shift data to parent component
    if (onShiftConfirm) {
      onShiftConfirm(shiftData);
    }

    // Refetch attendance details to update hasSchedule and other data
    await fetchAttendanceDetails();
  };

  const getDaySummary = () => {
    const totalElapsed = getTotalElapsedTime();
    const workTime = getWorkTime();
    const breakTime = getTotalBreakTime();
    const nursingTime = getTotalNursingTime();

    const formatTime = (time) => {
      if (time.hours > 0) {
        return `${time.hours} hr ${time.minutes} min`;
      }
      return `${time.minutes} min`;
    };

    return {
      totalHours: formatTime(totalElapsed),
      timeWorked: formatTime(workTime),
      breaks: formatTime(breakTime),
      nursing: formatTime(nursingTime),
    };
  };

  const getDaySummaryWithCustomEndTime = (endTime) => {
    if (!attendanceData) {
      return {
        totalHours: "0 min",
        timeWorked: "0 min",
        breaks: "0 min",
        nursing: "0 min",
      };
    }

    const attendanceInTime =
      attendanceData?.data?.attendance?.inTime || attendanceData?.attendanceDetails?.data?.attendance?.inTime;
    if (!attendanceInTime) {
      return {
        totalHours: "0 min",
        timeWorked: "0 min",
        breaks: "0 min",
        nursing: "0 min",
      };
    }

    const inTime = new Date(attendanceInTime).getTime();
    const outTime = new Date(endTime).getTime();
    const totalElapsedSeconds = Math.floor((outTime - inTime) / 1000);

    const totalBreakSeconds =
      attendanceData?.stats?.totalBreakSeconds || attendanceData?.attendanceDetails?.stats?.totalBreakSeconds || 0;
    const totalNursingBreakSeconds =
      attendanceData?.stats?.totalNursingBreakSeconds ||
      attendanceData?.attendanceDetails?.stats?.totalNursingBreakSeconds ||
      0;
    const workSeconds = totalElapsedSeconds - totalBreakSeconds - totalNursingBreakSeconds;

    const formatTime = (seconds) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours} hr ${minutes} min`;
      }
      return `${minutes} min`;
    };

    return {
      totalHours: formatTime(totalElapsedSeconds),
      timeWorked: formatTime(workSeconds),
      breaks: formatTime(totalBreakSeconds),
      nursing: formatTime(totalNursingBreakSeconds),
    };
  };

  const handleViewTimesheet = () => {
    navigate("/main/timesheet");
  };

  const handleStartBreak = async (type = "break") => {
    const activeBreak = getActiveBreak();
    const breakType = getBreakType();

    if (activeBreak) {
      // End break - validate for nursing break
      if (breakType === "nursing_break") {
        const totalNursingBreakSeconds =
          attendanceData?.stats?.totalNursingBreakSeconds ||
          attendanceData?.attendanceDetails?.stats?.totalNursingBreakSeconds ||
          0;
        const breakInTime = new Date(activeBreak.inTime).getTime();
        const currentBreakSeconds = Math.floor((currentTime - breakInTime) / 1000);
        const totalNursingSeconds = totalNursingBreakSeconds + currentBreakSeconds;
        const oneHourInSeconds = 3600;

        if (totalNursingSeconds > oneHourInSeconds) {
          setOpenBreakExceededModal(true);
          return;
        }
      }

      // End break - validate if current time exceeds shift end time + 3 hours
      const schedule = attendanceData?.schedule || attendanceData?.attendanceDetails?.schedule;
      if (schedule?.endTime && schedule?.startTime) {
        const attendanceDate =
          attendanceData?.data?.attendance?.date || attendanceData?.attendanceDetails?.data?.attendance?.date;

        if (attendanceDate) {
          const [startHours, startMinutes] = schedule.startTime.split(":").map(Number);
          const [endHours, endMinutes] = schedule.endTime.split(":").map(Number);

          const shiftDate = new Date(attendanceDate);
          const shiftStartTime = new Date(shiftDate);
          shiftStartTime.setHours(startHours, startMinutes, 0, 0);

          const isOvernightShift = endHours < startHours || (endHours === startHours && endMinutes < startMinutes);

          const shiftEndTime = new Date(shiftDate);
          if (isOvernightShift) {
            shiftEndTime.setDate(shiftEndTime.getDate() + 1);
          }
          shiftEndTime.setHours(endHours, endMinutes, 0, 0);

          const threeHoursAfterShift = new Date(shiftEndTime);
          threeHoursAfterShift.setHours(threeHoursAfterShift.getHours() + 3);

          if (currentTime > threeHoursAfterShift.getTime()) {
            setOpenBreakExceededModal(true);
            return;
          }
        }
      }

      // End break - call API
      try {
        const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        const employeeId = loggedInUser?.userId;

        if (!employeeId) {
          reduxDispatch(
            showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
          );
          return;
        }

        const timestamp = formatTimestamp(new Date());
        const action = breakType === "nursing_break" ? "nursing_break_end" : "break_end";

        await clockEntry(
          employeeId,
          {
            action,
            timestamp,
          },
          checkInRevamped
        );

        // Refetch attendance details after successful break end
        await fetchAttendanceDetails();

        const breakTypeName = breakType === "nursing_break" ? "nursing break" : "break";
        reduxDispatch(
          showSnackbar({
            type: AlertType.success,
            message: `Successfully ended ${breakTypeName}. Back to work!`,
            dispatch: reduxDispatch,
          })
        );
      } catch (error) {
        reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
      }
    } else {
      // Start break - call API
      try {
        const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        const employeeId = loggedInUser?.userId;

        if (!employeeId) {
          reduxDispatch(
            showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
          );
          return;
        }

        const timestamp = formatTimestamp(new Date());
        const action = type === "nursing" ? "nursing_break_start" : "break_start";

        await clockEntry(
          employeeId,
          {
            action,
            timestamp,
          },
          checkInRevamped
        );

        // Refetch attendance details after successful break start
        await fetchAttendanceDetails();

        const breakTypeName = type === "nursing" ? "nursing break" : "break";
        reduxDispatch(
          showSnackbar({
            type: AlertType.success,
            message: `Successfully started ${breakTypeName}. Enjoy your break!`,
            dispatch: reduxDispatch,
          })
        );
      } catch (error) {
        reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
      }
    }
  };

  const handleBreakMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBreakMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBreakSelect = () => {
    handleStartBreak("break");
    handleBreakMenuClose();
  };

  const handleNursingBreakSelect = () => {
    handleStartBreak("nursing");
    handleBreakMenuClose();
  };

  const handleBreakExceededModalClose = () => {
    setOpenBreakExceededModal(false);
  };

  const handleEndDayModalClose = () => {
    setOpenEndDayModal(false);
  };

  const handleClockOutSuccessModalClose = () => {
    setOpenClockOutSuccessModal(false);

    // Notify parent component to refresh attendance data
    if (onAttendanceUpdate) {
      onAttendanceUpdate();
    }
  };

  const handleViewTimesheetFromSuccess = () => {
    setOpenClockOutSuccessModal(false);
    navigate("/main/timesheet");
  };

  const handleMissedClockOutModalClose = () => {
    setOpenMissedClockOutModal(false);
  };

  const handleMissedClockOutSave = async (clockOutTime) => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const employeeId = loggedInUser?.userId;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      // Calculate day summary based on the selected clock-out time BEFORE API call
      const summaryWithCustomEndTime = getDaySummaryWithCustomEndTime(clockOutTime);
      setCachedDaySummary(summaryWithCustomEndTime);

      const timestamp = formatTimestamp(clockOutTime);
      await clockEntry(
        employeeId,
        {
          action: "check_out",
          timestamp,
        },
        checkInRevamped
      );

      // Close Missed Clock-Out Modal and open Success Modal
      setOpenMissedClockOutModal(false);
      await fetchAttendanceDetails();
      setOpenClockOutSuccessModal(true);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
    }
  };

  const handleEndDayConfirm = async () => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const employeeId = loggedInUser?.userId;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      // Capture day summary BEFORE clocking out (while still clocked in)
      const summaryBeforeClockOut = getDaySummary();
      setCachedDaySummary(summaryBeforeClockOut);

      const timestamp = formatTimestamp(new Date());
      await clockEntry(
        employeeId,
        {
          action: "check_out",
          timestamp,
        },
        checkInRevamped
      );

      // Close End Day Modal and open Success Modal
      setOpenEndDayModal(false);
      await fetchAttendanceDetails();
      setOpenClockOutSuccessModal(true);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
    }
  };

  const handleBreakExceededUpdate = async (endTime) => {
    const breakType = getBreakType();

    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const employeeId = loggedInUser?.userId;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      const timestamp = formatTimestamp(new Date(endTime));
      const action = breakType === "nursing_break" ? "nursing_break_end" : "break_end";

      await clockEntry(
        employeeId,
        {
          action,
          timestamp,
        },
        checkInRevamped
      );

      // Refetch attendance details after successful break end
      await fetchAttendanceDetails();

      setOpenBreakExceededModal(false);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
      setOpenBreakExceededModal(false);
    }
  };

  return (
    <>
      <div className="clock-in-card">
        <div className="clock-in-card__header">
          <div className="clock-in-card__timer-section">
            <div className="clock-in-card__timer-label para-body-m-medium" style={{ color: "#000000" }}>
              {getTimerLabel()}
            </div>
            <div className="clock-in-card__timer-display">
              <p className="heading-h2-semibold" style={{ color: "#000000" }}>
                <span>
                  {formatTimeUnit(getActiveBreak() ? getCurrentBreakTime().hours : getTotalElapsedTime().hours)}
                </span>
                <span>:</span>
                <span>
                  {formatTimeUnit(getActiveBreak() ? getCurrentBreakTime().minutes : getTotalElapsedTime().minutes)}
                </span>
                <span style={{ color: "#a8a8a8" }}>
                  :{formatTimeUnit(getActiveBreak() ? getCurrentBreakTime().seconds : getTotalElapsedTime().seconds)}
                </span>
              </p>
            </div>
          </div>
          <div className="clock-in-card__action">
            {canRecordTime && getIsClockedIn() &&
              !getActiveBreak() &&
              isWithinShiftTime() &&
              (isNursingBreakAllowed ? (
                <>
                  <ButtonWithDropdown
                    label="Start Break"
                    onClick={() => handleStartBreak("break")}
                    onDropdownClick={handleBreakMenuClick}
                    variant="contained"
                    color="secondary"
                    size="medium"
                  />
                  <Menu
                    anchorEl={anchorEl}
                    open={openBreakMenu}
                    onClose={handleBreakMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem value="Break" onClick={handleBreakSelect} />
                    <MenuItem value="Nursing" onClick={handleNursingBreakSelect} />
                  </Menu>
                </>
              ) : (
                <CustomButton
                  label="Start Break"
                  onClick={() => handleStartBreak("break")}
                  variant="contained"
                  color="secondary"
                  size="medium"
                />
              ))}
            {canRecordTime &&
              (getActiveBreak() ? (
                <CustomButton
                  label={getBreakType() === "break" ? "End Break" : "End Nursing Break"}
                  onClick={() => handleStartBreak()}
                  variant="contained"
                  color="primary"
                  size="large"
                />
              ) : (
                <CustomButton
                  label={getIsClockedIn() ? "Clock-Out" : "Clock-In"}
                  onClick={handleClockIn}
                  variant="contained"
                  color={getIsClockedIn() ? "error" : "primary"}
                  size={getIsClockedIn() ? "medium" : "large"}
                />
              ))}
          </div>
        </div>
        <div className="clock-in-card__footer">
          <div className="clock-in-card__stats">
            {getActiveBreak() ? (
              <>
                <div className="clock-in-card__stat-item">
                  <div className="clock-in-card__stat-icon">
                    <WorkIcon />
                  </div>
                  <span className="clock-in-card__stat-label para-body-m-medium pr-05">Time worked</span>
                  <span className="clock-in-card__stat-value para-body-m-medium">
                    {getWorkTime().hours > 0 && `${getWorkTime().hours} hr `}
                    {getWorkTime().minutes} min
                  </span>
                </div>
                <div className="clock-in-card__divider clock-in-card__divider--vertical" />
                <div className="clock-in-card__stat-item">
                  <div className="clock-in-card__stat-icon">
                    {getBreakType() === "break" && isNursingBreakAllowed ? <FeederIcon /> : <BurgerIcon />}
                  </div>
                  <span className="clock-in-card__stat-label para-body-m-medium pr-05">
                    {getBreakType() === "break" && isNursingBreakAllowed ? "Nursing" : "Breaks"}
                  </span>
                  {getBreakType() === "break" && isNursingBreakAllowed ? (
                    <div className="clock-in-card__stat-nursing">
                      <span className="clock-in-card__stat-value para-body-m-medium">
                        {getTotalNursingTime().hours} hr {getTotalNursingTime().minutes} min
                      </span>
                      <span className="clock-in-card__stat-max para-body-m-medium">(max 1 hr)</span>
                    </div>
                  ) : (
                    <span className="clock-in-card__stat-value para-body-m-medium">
                      {getTotalBreakTime().hours} hr {getTotalBreakTime().minutes} min
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="clock-in-card__stat-item">
                  <div className="clock-in-card__stat-icon">
                    <BurgerIcon />
                  </div>
                  <span className="clock-in-card__stat-label para-body-m-medium pr-05">Breaks</span>
                  <span className="clock-in-card__stat-value para-body-m-medium">
                    {getTotalBreakTime().hours} hr {getTotalBreakTime().minutes} min
                  </span>
                </div>
                {isNursingBreakAllowed && (
                  <>
                    <div className="clock-in-card__divider clock-in-card__divider--vertical" />
                    <div className="clock-in-card__stat-item">
                      <div className="clock-in-card__stat-icon">
                        <FeederIcon />
                      </div>
                      <span className="clock-in-card__stat-label para-body-m-medium pr-05">Nursing</span>
                      <div className="clock-in-card__stat-nursing">
                        <span className="clock-in-card__stat-value para-body-m-medium">
                          {getTotalNursingTime().hours} hr {getTotalNursingTime().minutes} min
                        </span>
                        <span className="clock-in-card__stat-max para-body-m-medium">(max 1 hr)</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="clock-in-card__timesheet-link para-body-m-medium" onClick={handleViewTimesheet}>
            View Timesheet
          </div>
        </div>
      </div>
      <SelectShiftModal open={openSelectShift} onClose={handleSelectShiftClose} onConfirm={handleSelectShiftConfirm} />
      <BreakTimeExceededModal
        open={openBreakExceededModal}
        onClose={handleBreakExceededModalClose}
        onUpdate={handleBreakExceededUpdate}
        breakType={getBreakType()}
        breakStartTime={getActiveBreak() ? new Date(getActiveBreak().inTime).getTime() : null}
        totalNursingBreakSeconds={
          attendanceData?.stats?.totalNursingBreakSeconds ||
          attendanceData?.attendanceDetails?.stats?.totalNursingBreakSeconds ||
          0
        }
        shiftEndTime={getShiftEndTime()?.getTime() || null}
      />
      <EndDayModal
        open={openEndDayModal}
        onClose={handleEndDayModalClose}
        onConfirm={handleEndDayConfirm}
        daySummary={getDaySummary()}
      />
      <MissedClockOutModal
        open={openMissedClockOutModal}
        onClose={handleMissedClockOutModalClose}
        onSave={handleMissedClockOutSave}
        clockInTime={getClockInTime()}
        shiftDate={getShiftDate()}
        shiftEndTime={getShiftEndTime()}
      />
      <ClockOutSuccessModal
        open={openClockOutSuccessModal}
        onClose={handleClockOutSuccessModalClose}
        onViewTimesheet={handleViewTimesheetFromSuccess}
        daySummary={cachedDaySummary || getDaySummary()}
      />
    </>
  );
}

ClockInCard.propTypes = {
  onShiftConfirm: PropTypes.func,
  onAttendanceUpdate: PropTypes.func,
};

export default ClockInCard;
